import { action, observable, computed } from 'mobx';
import { useStaticRendering } from 'mobx-react';

import WPAPI from 'wpapi';
import jwtDecode from 'jwt-decode';
import ApiCore from './ApiCore';
import Config from '../config';

// Helpers
import {
  calculateVirtueProgress,
  getCompletedVirtueLessonsByKey,
} from '../helpers';

const NUMBER_OF_VIRTUES = 52;
const wp = new WPAPI({ endpoint: Config.apiUrl });

const isServer = !process.browser;
useStaticRendering(isServer);

class UserStore {
  @observable user = {};

  @observable subscription = {};

  @observable loggedIn = false;

  @observable users = [];

  @observable resetPassword = false;

  @observable modalOpen = false;

  @observable latestVirtue = 0;

  @computed get currentVirtueProgress() {
    return calculateVirtueProgress(this.user.acf);
  }

  @computed get numberOfCompletedLessons() {
    return getCompletedVirtueLessonsByKey(this.user.acf, true);
  }

  @computed get numberOfUncompletedLessons() {
    return NUMBER_OF_VIRTUES - this.numberOfCompletedLessons;
  }

  @computed get favoriteVirtues() {
    return this.user.acf ? this.user.acf.favorite_virtues : [];
  }

  @computed get selectedVirtues() {
    return this.user.acf ? this.user.acf.selected_virtues : [];
  }

  /**
   * Watch token existence and expiration
   */
  checkToken() {
    const token = this.getToken();

    const tokenExpired = token
      ? jwtDecode(token).exp < new Date().getTime() / 1000
      : false;

    const loggedIn = !!token && !tokenExpired;
    this.setLoggedIn(loggedIn);

    return loggedIn;
  }

  /**
   * Sets the user token to localStorage
   * @param {string} token
   */
  setToken = (token, callback = null) => {
    localStorage.setItem(Config.AUTH_TOKEN, token);

    if (callback !== null) callback();
  }

  /**
   * Retrieves the token from localStorage
   */
  getToken = () => localStorage.getItem(Config.AUTH_TOKEN);

  /**
   * Cleares the token from localStorage
   */
  @action async logout() {
    localStorage.removeItem(Config.AUTH_TOKEN);

    this.loggedIn = false;
    this.user = {};
  }

  /**
   * Sets the lesson's completed state
   * @param {string} virtueId
   * @param {string} lessonId
   * @param {boolean} state
   */
  @action complete(virtueId, lessonId, state) {
    const fieldKey = `virtue_${virtueId}_lesson_${Number(lessonId)
      + 1}_completed`;

    const newState = !state;

    this.user.acf[fieldKey] = newState;
    this.user.acf.current_virtue = virtueId;

    if (this.currentVirtueProgress === 100) {
      this.user.acf.last_virtue_completed = virtueId;
    }

    this.updateCompleted(newState, virtueId, lessonId);
  }

  /**
   * Toggles the virtue as user favorite
   * @param {string} virtueId
   */
  @action async favorite(virtueId) {
    if (this.user.acf.favorite_virtues.indexOf(virtueId) === -1) {
      if (!this.user.acf.favorite_virtues) {
        this.user.acf.favorite_virtues = [];
      }
      this.user.acf.favorite_virtues.push(virtueId);
    } else {
      this.user.acf.favorite_virtues.splice(
        this.user.acf.favorite_virtues.indexOf(virtueId),
        1,
      );
    }

    this.updateFavorites(this.user.acf.favorite_virtues);
  }

  @action async selected(virtueId) {
    if (virtueId) {
      if (this.user.acf.selected_virtues.indexOf(virtueId) === -1) {
        if (!this.user.acf.selected_virtues) {
          this.user.acf.selected_virtues = [];
        }
        this.user.acf.selected_virtues.push(virtueId);
      } else {
        this.user.acf.selected_virtues.splice(
          this.user.acf.selected_virtues.indexOf(virtueId),
          1,
        );
      }
    } else {
      this.user.acf.selected_virtues = [];
    }
    this.updateSelected(this.user.acf.selected_virtues);
  }

  /**
   * Sets the JWT token in local storage to be used for auth
   * @param {object} data
   */
  @action async login(username, password) {
    return ApiCore.post('/jwt-auth/v1/token', {
      username,
      password,
    })
      .then((response) => {
        const { data } = response;

        this.setToken(data.token);
        this.show();

        return data;
      })
      .catch(error => Promise.reject(error.response));
  }

  @action async getSubscription(customerId) {
    if (this.subscription.billing) return this.subscription;

    const subscriptions = await ApiCore.get(`/wc/v1/subscriptions?order=desc&customer=${customerId}`)
      .then((response) => {
        this.subscription = response.data && response.data.length ? response.data[0] : {};

        return response;
      })
      .catch(error => Promise.reject(error.response));

    return subscriptions;
  }

  @action async cancelSubscription(customerId) {
    let subscription = {};
    await this.getSubscription(customerId).then((response) => {
      subscription = response;
    });

    const subscriptions = await ApiCore.post(`/wc/v1/subscriptions/${subscription.id}`, {
      status: 'pending-cancel',
    })
      .then((response) => {
        this.subscription = response.data;
      });

    return subscriptions;
  }

  /**
   * Shows the currently logged in user based on the token placed in localStorage
   */
  @action async show() {
    const token = this.getToken();

    if (token && typeof this.user.acf === 'undefined') {
      wp.setHeaders('Authorization', `Bearer ${token}`);
      return wp
        .users()
        .me()
        .then((data) => {
          const user = { ...data };

          // Adds extra properties which does not exist on the basic user init
          const properties = [
            { name: 'last_virtue_completed', defaultValue: '' },
            { name: 'favorite_virtues', defaultValue: [] },
            { name: 'selected_virtues', defaultValue: [] },
            { name: 'current_virtue', defaultValue: '' },
            { name: 'profile_image', defaultValue: '' },
          ];

          // Maps user properties for virtues and its lessons
          for (let virtueIndex = 1; virtueIndex < 53; virtueIndex++) {
            for (let lessonIndex = 1; lessonIndex < 8; lessonIndex++) {
              properties.push(
                {
                  name: `virtue_${virtueIndex}_lesson_${lessonIndex}_completed`,
                  defaultValue: false,
                },
                { name: `virtue_${virtueIndex}_progress`, defaultValue: '' },
                { name: `virtue_${virtueIndex}_journal`, defaultValue: '' },
              );
            }
          }

          if (typeof user.acf !== 'undefined' && user.acf.length === 0) {
            user.acf = {};
          }

          // Creates the mappings
          properties.map((property) => {
            if (
              typeof user.acf[property.name] === 'undefined'
              || user.acf[property.name] === null
              || user.acf[property.name] === ''
            ) {
              user.acf[property.name] = property.defaultValue;
            }
          });

          this.user = user;

          return user;
        })
        .catch(() => {
          // if (!err.data || err.data.status === 403) {
          //   tokenExpired();
          // }
        });
    }
    return this.user;
  }

  /**
   * Returns all users
   */
  @action async index() {
    return new Promise((resolve) => {
      ApiCore.get('/wp/v2/users').then((response) => {
        this.users = response.data;
      });
      resolve();
    });
  }

  /**
   * Updates the favorite virtues of a user
   * @param {array} favorites
   * @param {string} userId
   */
  @action updateFavorites(favorites) {
    return new Promise((resolve) => {
      ApiCore.post(`/acf/v3/users/${this.user.id}`, {
        fields: {
          favorite_virtues: favorites,
        },
      })
        .then(() => resolve());
    });
  }

  /**
   *
   * @param {array} selected
   */
  @action updateSelected(selected) {
    return new Promise((resolve) => {
      ApiCore.post(`/acf/v3/users/${this.user.id}`, {
        fields: {
          selected_virtues: selected,
        },
      }).then(() => resolve());
    });
  }

  /**
   * Updates the favorite virtues of a user
   * @param {array} favorites
   * @param {string} userId
   */
  @action updateCompleted(state, virtueId, lessonId) {
    const fieldKey = `virtue_${virtueId}_lesson_${Number(lessonId)
      + 1}_completed`;
    return new Promise((resolve) => {
      ApiCore.post(`/acf/v3/users/${this.user.id}`,
        {
          fields: {
            [fieldKey]: state,
            current_virtue: virtueId,
            last_virtue_completed: this.user.acf.last_virtue_completed,
          },
        })
        .then(() => resolve());
    });
  }

  /**
   * Updates the journal to a given virtue.
   * @param {string} virtueId Virtue id
   * @param {string} userId User id
   * @param {boolean} progress Current progress of the virtue
   */
  @action updateJournal(journal) {
    const fieldKey = `virtue_${this.latestVirtue}_journal`;

    this.user.acf[fieldKey] = journal;
    this.user.acf.last_virtue_journal = this.latestVirtue;

    return new Promise((resolve) => {
      ApiCore.post(`/acf/v3/users/${this.user.id}`, {
        fields: {
          [fieldKey]: journal,
          last_virtue_journal: this.latestVirtue,
        },
      })
        .then(() => resolve());
    });
  }

  /**
   * Updates the progress of a virtue.
   * @param {string} virtueId Virtue id
   * @param {string} userId User id
   * @param {boolean} progress Current progress of the virtue
   */
  @action updateVirtueProgress(virtueId, userId, progress) {
    const fieldKey = `virtue_${virtueId}_progress`;

    // updates frontend
    this.user.acf[fieldKey] = progress;

    return new Promise((resolve) => {
      ApiCore.post(`/acf/v3/users/${userId}`, {
        fields: {
          [fieldKey]: progress,
        },
      }).then(() => resolve());
    });
  }

  /**
   *
   * @param {integer} number
   */
  @action updateFrequency(number) {
    const fieldKey = 'frequency';
    return new Promise((resolve) => {
      ApiCore.post(`/acf/v3/users/${this.user.id}`, {
        fields: {
          [fieldKey]: number,
        },
      }).then(() => resolve());
    });
  }

  @action async uploadProfileImage(image) {
    if (typeof this.user.acf !== 'undefined' && this.user.acf.length === 0) {
      this.user.acf = {};
    }

    this.user.acf.profile_image = image;

    const fieldKey = 'profile_image';

    ApiCore.post(`/acf/v3/users/${this.user.id}`, {
      fields: {
        [fieldKey]: image,
      },
    });
  }

  @action setResetPassword(set) {
    this.resetPassword = set;
  }

  @action toggleModal() {
    this.modalOpen = !this.modalOpen;
  }

  @action setLatestVirtue(id) {
    this.latestVirtue = id;
  }

  @action setLoggedIn(loggedIn) {
    this.loggedIn = loggedIn;
  }
}

let userStore = null;

export default function initializeUserStore(initialData) {
  if (isServer) {
    return new UserStore(isServer, initialData);
  }

  if (userStore === null) {
    userStore = new UserStore(isServer, initialData);
  }

  return userStore;
}
